"use client";

import { Heart, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { ActivityPostLikeListItem } from "@/actions/activity-post";
import type { LikeListItem } from "@/actions/post";
import type { SocialFeedLikeListItem } from "@/actions/social-feed-interaction";
import { ConnectButton } from "@/components/buddy";
import { ProfileLink } from "@/components/profile/profile-link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { ListRowSkeleton } from "@/components/ui/list-row-skeleton";
import { Separator } from "@/components/ui/separator";
import type { SocialFeedEntity } from "@/db/schema/social-feed";
import { useActivityPostLikes } from "@/hooks/use-activity-likes";
import { useActivitySessionLikes } from "@/hooks/use-activity-session-likes";
import { useBuddyRelationships } from "@/hooks/use-buddy-relationships";
import { usePostLikes } from "@/hooks/use-posts";
import { useSocialFeedLikes } from "@/hooks/use-social-feed-interactions";
import type { BuddyRelationship } from "@/lib/buddy/relationship";
import { formatAbsoluteDay } from "@/lib/dates/relative-time";

type EntityType = "post" | "activity" | "activity-session" | "social";

interface LikesSheetPostProps {
  entityType?: "post";
  postId?: number;
  activityPostId?: never;
  entityId?: never;
  socialEntityType?: SocialFeedEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
}

interface LikesSheetActivityProps {
  entityType: "activity";
  postId?: never;
  activityPostId?: number;
  entityId?: never;
  socialEntityType?: SocialFeedEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
}

interface LikesSheetActivitySessionProps {
  entityType: "activity-session";
  postId?: never;
  activityPostId?: never;
  entityId: number;
  socialEntityType?: SocialFeedEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
}

interface LikesSheetSocialProps {
  entityType: "social";
  postId?: never;
  activityPostId?: never;
  entityId: number;
  socialEntityType: SocialFeedEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
}

type LikesSheetProps =
  | LikesSheetPostProps
  | LikesSheetActivityProps
  | LikesSheetActivitySessionProps
  | LikesSheetSocialProps;

// Unified like type for UI
type UILike = {
  id: string;
  displayName: string;
  username: string | null;
  avatarUrl: string | null;
  likedAt: Date | number;
  isBuddy?: boolean;
  isBlocked?: boolean;
};

export function LikesSheet(props: LikesSheetProps) {
  const { open, onOpenChange, currentUserId } = props;
  const entityType: EntityType = props.entityType ?? "post";
  const socialEntityType =
    entityType === "social" ? (props.socialEntityType ?? null) : null;
  const entityId =
    entityType === "activity"
      ? (props.activityPostId ?? 0)
      : entityType === "activity-session" || entityType === "social"
        ? (props.entityId ?? 0)
        : (props.postId ?? 0);

  const [query, setQuery] = useState("");

  const likesEnabled = open && entityId > 0;

  // Use appropriate hooks based on entity type
  const postLikesQuery = usePostLikes(
    entityType === "post" ? (entityId ?? null) : null,
    { enabled: likesEnabled && entityType === "post" },
  );
  const activityLikesQuery = useActivityPostLikes(
    entityType === "activity" ? (entityId ?? null) : null,
    { enabled: likesEnabled && entityType === "activity" },
  );
  const activitySessionLikesQuery = useActivitySessionLikes(
    entityType === "activity-session" ? (entityId ?? null) : null,
    { enabled: likesEnabled && entityType === "activity-session" },
  );
  const socialLikesQuery = useSocialFeedLikes(
    socialEntityType,
    entityType === "social" ? (entityId ?? null) : null,
    { enabled: likesEnabled && entityType === "social" },
  );

  const likesQuery =
    entityType === "activity"
      ? activityLikesQuery
      : entityType === "activity-session"
        ? activitySessionLikesQuery
        : entityType === "social"
          ? socialLikesQuery
          : postLikesQuery;

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = likesQuery;

  // Transform to unified format
  const likes = ((): UILike[] => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.likes.map((like): UILike => {
        if (entityType === "post") {
          const postLike = like as LikeListItem;
          return {
            id: postLike.id,
            displayName: postLike.displayName,
            username: postLike.username,
            avatarUrl: postLike.avatarUrl,
            likedAt: postLike.likedAt,
            isBuddy: postLike.id === currentUserId ? false : postLike.isBuddy,
            isBlocked: Boolean(postLike.isBlocked),
          };
        }
        const activityLike = like as ActivityPostLikeListItem;
        if (entityType === "social") {
          const socialLike = like as SocialFeedLikeListItem;
          return {
            id: socialLike.id,
            displayName: socialLike.displayName,
            username: socialLike.username,
            avatarUrl: socialLike.avatarUrl,
            likedAt: socialLike.likedAt,
            isBuddy: undefined,
            isBlocked: false,
          };
        }
        if (entityType === "activity-session") {
          const activitySessionLike = like as {
            userId: string;
            displayName: string;
            username: string | null;
            avatarUrl: string | null;
            likedAt: Date | number;
          };
          return {
            id: activitySessionLike.userId,
            displayName: activitySessionLike.displayName,
            username: activitySessionLike.username,
            avatarUrl: activitySessionLike.avatarUrl,
            likedAt: activitySessionLike.likedAt,
            isBuddy: undefined,
            isBlocked: false,
          };
        }
        return {
          id: activityLike.id,
          displayName: activityLike.displayName,
          username: activityLike.username,
          avatarUrl: activityLike.avatarUrl,
          likedAt: activityLike.likedAt,
          isBuddy: undefined, // Activity likes don't have buddy status yet
          isBlocked: Boolean(activityLike.isBlocked),
        };
      }),
    );
  })();

  useEffect(() => {
    if (!error) return;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Failed to load likes";
    toast.error(message);
  }, [error]);

  // One lookup for the whole page of likers, refreshed as more pages load.
  // The like endpoints only carry `isBuddy` for post likes, and never carry
  // pending-request state, so the quick-add button gets its own source.
  const { relationshipOf } = useBuddyRelationships(
    likes.map((like) => like.id),
  );

  const filteredLikes = (() => {
    if (!query) {
      return likes;
    }
    const lowered = query.toLowerCase();
    return likes.filter((item) => {
      const name = item.displayName?.toLowerCase() ?? "";
      const username = item.username?.toLowerCase() ?? "";
      return name.includes(lowered) || username.includes(lowered);
    });
  })();

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="z-200 mx-auto w-full max-w-lg rounded-t-[28px] border-border bg-background">
        <DrawerHeader className="pb-0 text-center">
          <DrawerTitle className="text-base font-semibold text-text-primary">
            Likes
          </DrawerTitle>
        </DrawerHeader>

        <div className="space-y-2 px-4 py-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-text-secondary" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="pl-9"
            />
          </div>
        </div>

        <Separator className="bg-border" />

        <div className="no-scrollbar flex h-[55vh] flex-col gap-4 overflow-y-auto px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {entityId < 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-text-secondary">
              <p className="text-sm">
                Likes will appear once the post is live.
              </p>
            </div>
          ) : isLoading ? (
            <ListRowSkeleton rowCount={5} className="-mx-3" />
          ) : filteredLikes.length === 0 ? (
            likes.length === 0 ? (
              <EmptyState
                icon={Heart}
                title="No likes yet"
                description="Be the first to like this."
                compact
                className="flex-1"
              />
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-text-secondary">
                <p className="text-sm">No matches for your search.</p>
              </div>
            )
          ) : (
            filteredLikes.map((like) => (
              <LikeRow
                key={like.id}
                like={like}
                isOwnLike={like.id === currentUserId}
                relationship={relationshipOf(like.id)}
              />
            ))
          )}

          {hasNextPage && entityId > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="mx-auto w-fit text-text-secondary"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "View more"}
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface LikeRowProps {
  like: UILike;
  isOwnLike?: boolean;
  relationship: BuddyRelationship | undefined;
}

function LikeRow({ like, isOwnLike, relationship }: LikeRowProps) {
  const isBlocked = Boolean(like.isBlocked);
  const secondary = like.username
    ? `@${like.username}`
    : isBlocked
      ? "Blocked profile"
      : formatRelative(like.likedAt);

  return (
    <div className="flex items-center justify-between gap-3">
      {isBlocked ? (
        <div className="flex flex-1 items-center gap-3">
          <Avatar className="h-10 w-10 border border-border">
            <AvatarImage src={undefined} />
            <AvatarFallback>
              {initials(like.displayName || like.username || "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-text-primary">
              {like.displayName || like.username}
            </span>
            <span className="text-xs text-text-secondary">{secondary}</span>
          </div>
        </div>
      ) : (
        <ProfileLink
          handle={like.username}
          fallbackAs="div"
          className="flex flex-1 items-center gap-3 transition-opacity hover:opacity-80"
        >
          <Avatar className="h-10 w-10 border border-border">
            <AvatarImage src={like.avatarUrl ?? undefined} />
            <AvatarFallback>
              {initials(like.displayName || like.username || "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-text-primary">
              {like.displayName || like.username}
            </span>
            <span className="text-xs text-text-secondary">{secondary}</span>
          </div>
        </ProfileLink>
      )}

      {!isBlocked && !isOwnLike && (
        <ConnectButton
          targetUserId={like.id}
          targetName={like.displayName || like.username}
          relationship={relationship}
          surface="post_likes"
          buddyFallback={
            <Badge variant="outline" className="border-success text-success">
              Buddies
            </Badge>
          }
        />
      )}
    </div>
  );
}

const initials = (value: string) => {
  const cleaned = value.trim();
  if (!cleaned) {
    return "";
  }
  const parts = cleaned.split(" ");
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2).toUpperCase() ?? "";
  }
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
};

const formatRelative = (value: Date | number) => formatAbsoluteDay(value);
